import { useRef, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { 
  Play,
  ChevronRight,
  EyeOff,
  Database,
  AlertTriangle,
  ClipboardList,
  TrendingUp,
  Clock,
  Users,
  LayoutDashboard,
  Footprints,
  Timer,
  ListOrdered,
  Map,
  UserCheck,
  Bell,
  Quote
} from 'lucide-react';

// Animated Counter Component
const AnimatedStat = ({ value, suffix = '', label }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [count, setCount] = useState(0);
  
  useEffect(() => {
    if (isInView) {
      let start = 0;
      const end = value;
      const duration = 2000;
      const increment = end / (duration / 16);
      
      const timer = setInterval(() => {
        start += increment;
        if (start >= end) {
          setCount(end);
          clearInterval(timer);
        } else {
          setCount(Math.floor(start));
        }
      }, 16);
      
      return () => clearInterval(timer);
    }
  }, [isInView, value]);
  
  return (
    <div ref={ref} className="text-center" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="text-4xl md:text-5xl font-bold font-heading text-foreground mb-2">
        <span>{count.toLocaleString()}</span>
        <span className="text-primary">{suffix}</span>
      </div>
      <p className="text-sm text-muted-foreground font-medium uppercase tracking-wider">{label}</p>
    </div>
  );
};

// Section wrapper with animation
const AnimatedSection = ({ children, className = '', delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
      transition={{ duration: 0.6, delay, ease: "easeOut" }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const Home = () => {
  const problems = [
    { title: "Passive Monitoring", desc: "Cameras record everything but inform nothing. Incidents surface only after they happen.", icon: EyeOff },
    { title: "Invisible Behavior", desc: "POS shows transactions, but browsing patterns and lost opportunities remain hidden.", icon: Database },
    { title: "Operational Blindness", desc: "Queues, staffing gaps, and conversion leaks stay invisible to headquarters.", icon: AlertTriangle },
    { title: "Manual Audits", desc: "Store audits are slow, expensive, and based on intuition rather than data.", icon: ClipboardList }
  ];

  const outcomes = [
    { icon: TrendingUp, title: "Increase Conversion", impact: "Identify missed traffic and non-buyers", method: "Actionable heatmaps and zone conversion metrics" },
    { icon: Clock, title: "Reduce Wait Time", impact: "Eliminate checkout friction and drop-outs", method: "Real-time alerts triggered by queue thresholds" },
    { icon: Users, title: "Improve Productivity", impact: "Align staffing with actual traffic peaks", method: "Automated staff presence and interaction reports" },
    { icon: LayoutDashboard, title: "Optimize Layout", impact: "Validate floor designs and display performance", method: "Dwell time analysis and aisle engagement data" }
  ];

  const modules = [
    { name: "Footfall Analytics", icon: Footprints },
    { name: "Dwell Time", icon: Timer },
    { name: "Queue Management", icon: ListOrdered },
    { name: "Heatmaps", icon: Map },
    { name: "Staff Activity", icon: UserCheck },
    { name: "Real-time Alerts", icon: Bell }
  ];

  const testimonials = [
    {
      quote: "ProAct AI reduced our queue abandonment by 35% in the first quarter. The ROI was visible within weeks.",
      author: "Operations Director",
      company: "National Retail Chain",
      metric: "35% reduction"
    },
    {
      quote: "Finally, we can see what's happening across all our stores in real-time. Game changer for our operations team.",
      author: "VP of Retail",
      company: "Fashion Group",
      metric: "500+ stores"
    },
    {
      quote: "The staff optimization insights alone justified the investment. We're now properly staffed during peak hours.",
      author: "Store Manager",
      company: "Premium Boutique",
      metric: "24% efficiency gain"
    }
  ];

  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section data-testid="hero-section" className="relative py-8 lg:py-12 overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-50"></div>
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-primary/5 to-transparent"></div>
        
        <div className="container-custom relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <AnimatedSection>
              <div className="flex flex-wrap gap-2 mb-6">
                <span className="bg-secondary text-foreground px-3 py-1.5 rounded-full text-xs font-semibold border border-border">Works with existing cameras</span>
                <span className="bg-primary/10 text-primary px-3 py-1.5 rounded-full text-xs font-semibold">Deploy in 1 day</span>
              </div>
              
              <h1 className="text-4xl lg:text-6xl font-extrabold font-heading text-foreground leading-[1.1] mb-6">
                Turn existing CCTV into real-time{' '}
                <span className="text-gradient">retail intelligence.</span>
              </h1>
              
              <p className="text-lg text-muted-foreground max-w-lg leading-relaxed mb-8">
                Footfall, dwell time, queues, staff activity, and hotspots — measured and visualized across every store.
              </p>
              
              <div className="flex flex-wrap gap-4">
                <Link 
                  to="/contact" 
                  data-testid="hero-cta-primary"
                  className="btn-primary inline-flex items-center"
                >
                  Request Demo
                  <ChevronRight className="ml-2 w-5 h-5" />
                </Link>
                <button 
                  data-testid="hero-cta-secondary"
                  className="btn-secondary inline-flex items-center"
                >
                  <Play className="w-5 h-5 mr-2 text-primary" />
                  Watch Overview
                </button>
              </div>
            </AnimatedSection>
            
            <AnimatedSection delay={0.2}>
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-tr from-primary/20 to-accent/20 rounded-3xl blur-3xl opacity-30"></div>
                <div className="relative aspect-video w-full rounded-2xl overflow-hidden shadow-2xl border border-border bg-foreground">
                  <iframe 
                    className="absolute inset-0 w-full h-full"
                    src="https://www.youtube.com/embed/wmgEsYv08MU?autoplay=1&mute=1&loop=1&playlist=wmgEsYv08MU&rel=0" 
                    title="ProAct AI Product Demo"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    loading="lazy"
                  ></iframe>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Shark Tank India Banner */}
      <section data-testid="shark-tank-section" className="w-full">
        <img 
          src="https://customer-assets.emergentagent.com/job_better-site-13/artifacts/d95rp0nd_85ed0a6d-a430-4814-9627-7cabb8b7635c.png" 
          alt="As Seen On Shark Tank India" 
          className="w-full h-auto object-cover"
        />
      </section>

      {/* Animated Stats Section */}
      <section data-testid="stats-section" className="py-12 bg-secondary/50 border-y border-border">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <AnimatedStat value={500} suffix="+" label="Retail Stores" />
            <AnimatedStat value={12000} suffix="+" label="Connected Cameras" />
            <AnimatedStat value={45} label="Major Cities" />
            <AnimatedStat value={24} suffix="/7" label="Monitoring" />
          </div>
        </div>
      </section>

      {/* Trusted By / Clients Section */}
      <section data-testid="clients-section" className="py-16 bg-white">
        <div className="container-custom">
          <AnimatedSection>
            <div className="text-center mb-12">
              <h2 className="text-2xl lg:text-3xl font-bold font-heading text-foreground mb-3">
                Trusted By Leading Brands
              </h2>
              <p className="text-muted-foreground">
                Powering retail intelligence for India's top enterprises
              </p>
            </div>
          </AnimatedSection>
          
          <AnimatedSection delay={0.1}>
            <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16">
              {/* RJ Corp */}
              <div className="flex items-center justify-center p-4 hover:scale-105 transition-all duration-300">
                <img 
                  src="/clients/rjcorp.jpeg" 
                  alt="RJ Corp Retail" 
                  className="h-24 md:h-32 w-auto object-contain"
                />
              </div>
              
              {/* Dava India */}
              <div className="flex items-center justify-center p-4 hover:scale-105 transition-all duration-300">
                <img 
                  src="/clients/davaindia.jpeg" 
                  alt="Dava India" 
                  className="h-24 md:h-32 w-auto object-contain"
                />
              </div>
              
              {/* Stellaro Brands */}
              <div className="flex items-center justify-center p-4 hover:scale-105 transition-all duration-300">
                <img 
                  src="/clients/stellaro.jpeg" 
                  alt="Stellaro Brands" 
                  className="h-24 md:h-32 w-auto object-contain"
                />
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Problem Section */}
      <section data-testid="problem-section" className="section-padding bg-white">
        <div className="container-custom">
          <AnimatedSection>
            <div className="max-w-2xl mb-12">
              <h2 className="text-3xl lg:text-4xl font-bold font-heading text-foreground mb-4">
                The Visibility Gap
              </h2>
              <p className="text-lg text-muted-foreground">
                Standard recording is no longer enough for competitive retail operations.
              </p>
            </div>
          </AnimatedSection>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {problems.map((problem, i) => (
              <AnimatedSection key={i} delay={i * 0.1}>
                <div 
                  data-testid={`problem-card-${i}`}
                  className="card-base p-6 h-full hover:-translate-y-1"
                >
                  <div className="w-12 h-12 rounded-xl bg-secondary flex items-center justify-center mb-4 text-muted-foreground">
                    <problem.icon className="w-6 h-6" />
                  </div>
                  <h3 className="text-lg font-bold font-heading text-foreground mb-2">{problem.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{problem.desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Outcomes Section */}
      <section data-testid="outcomes-section" className="section-padding bg-foreground text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-transparent"></div>
        
        <div className="container-custom relative z-10">
          <AnimatedSection>
            <div className="text-center max-w-2xl mx-auto mb-12">
              <h2 className="text-3xl lg:text-4xl font-bold font-heading mb-4">Business Value Delivered</h2>
              <p className="text-white/70 text-lg">Transforming standard surveillance into measurable competitive advantage.</p>
            </div>
          </AnimatedSection>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {outcomes.map((outcome, i) => (
              <AnimatedSection key={i} delay={i * 0.1}>
                <div 
                  data-testid={`outcome-card-${i}`}
                  className="flex bg-white/5 backdrop-blur-sm p-6 rounded-2xl border border-white/10 hover:border-white/20 transition-all"
                >
                  <div className="flex-shrink-0 w-14 h-14 bg-primary rounded-xl flex items-center justify-center text-white mr-5">
                    <outcome.icon size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold font-heading mb-1">{outcome.title}</h3>
                    <p className="text-primary text-sm font-semibold mb-1">{outcome.impact}</p>
                    <p className="text-white/60 text-sm">{outcome.method}</p>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Modules Section - Bento Grid */}
      <section data-testid="modules-section" className="section-padding bg-white">
        <div className="container-custom">
          <AnimatedSection>
            <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-4">
              <div>
                <h2 className="text-3xl lg:text-4xl font-bold font-heading text-foreground mb-2">The Intelligence Stack</h2>
                <p className="text-muted-foreground">Modular components designed for immediate operational ROI.</p>
              </div>
              <Link 
                to="/solutions" 
                data-testid="modules-cta"
                className="bg-foreground text-white font-semibold px-6 py-3 rounded-full flex items-center hover:bg-primary transition-all text-sm"
              >
                Explore All Modules <ChevronRight className="ml-2 w-4 h-4" />
              </Link>
            </div>
          </AnimatedSection>
          
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {modules.map((module, i) => (
              <AnimatedSection key={i} delay={i * 0.1}>
                <div 
                  data-testid={`module-card-${i}`}
                  className="card-base p-8 text-center hover:-translate-y-1 group"
                >
                  <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-4 text-primary group-hover:bg-primary group-hover:text-white transition-all">
                    <module.icon className="w-6 h-6" />
                  </div>
                  <h3 className="text-base font-bold font-heading text-foreground">{module.name}</h3>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Dashboard Preview Section */}
      <section data-testid="dashboard-section" className="section-padding bg-foreground text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-transparent to-accent/10"></div>
        <div className="container-custom relative z-10">
          <AnimatedSection>
            <div className="text-center max-w-3xl mx-auto mb-12">
              <h2 className="text-3xl lg:text-4xl font-bold font-heading mb-4">
                See It In Action
              </h2>
              <p className="text-white/70 text-lg">
                Uncover insights that were not possible before — driving critical decisions for your business.
              </p>
            </div>
          </AnimatedSection>
          
          <AnimatedSection delay={0.2}>
            <div className="relative max-w-5xl mx-auto">
              <div className="absolute -inset-4 bg-gradient-to-tr from-cyan-400/30 via-purple-500/20 to-pink-500/30 rounded-3xl blur-2xl opacity-50"></div>
              <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-white/10">
                <img 
                  src="https://customer-assets.emergentagent.com/job_better-site-13/artifacts/30tjmmjt_Dashboard.jpeg" 
                  alt="ProAct AI Dashboard - Footfall Analytics, Gender Split, Visitor Patterns, Dwell Time"
                  className="w-full h-auto"
                />
              </div>
            </div>
          </AnimatedSection>
          
          <AnimatedSection delay={0.3}>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-12">
              <div className="text-center">
                <div className="text-2xl font-bold text-cyan-400 mb-1">Footfall</div>
                <p className="text-white/60 text-sm">Weekly trends</p>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-400 mb-1">Demographics</div>
                <p className="text-white/60 text-sm">Gender split analysis</p>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-pink-400 mb-1">Behaviour</div>
                <p className="text-white/60 text-sm">Visitor patterns</p>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-cyan-400 mb-1">Dwell Time</div>
                <p className="text-white/60 text-sm">Engagement metrics</p>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Testimonials Section */}
      <section data-testid="testimonials-section" className="section-padding bg-secondary/30">
        <div className="container-custom">
          <AnimatedSection>
            <div className="text-center max-w-2xl mx-auto mb-12">
              <h2 className="text-3xl lg:text-4xl font-bold font-heading text-foreground mb-4">
                Trusted by Industry Leaders
              </h2>
              <p className="text-muted-foreground text-lg">
                See what operations teams are saying about ProAct AI.
              </p>
            </div>
          </AnimatedSection>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, i) => (
              <AnimatedSection key={i} delay={i * 0.15}>
                <div 
                  data-testid={`testimonial-card-${i}`}
                  className="bg-white p-8 rounded-2xl border border-border h-full flex flex-col"
                >
                  <Quote className="w-10 h-10 text-primary/20 mb-4" />
                  <p className="text-foreground font-medium leading-relaxed flex-grow mb-6">
                    "{testimonial.quote}"
                  </p>
                  <div className="border-t border-border pt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-semibold text-foreground text-sm">{testimonial.author}</p>
                        <p className="text-muted-foreground text-xs">{testimonial.company}</p>
                      </div>
                      <div className="bg-primary/10 text-primary px-3 py-1 rounded-full text-xs font-bold">
                        {testimonial.metric}
                      </div>
                    </div>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
